<template>
  <v-container class="pt-7 pb-11">
    <h1 class="text-h4 mb-5">{{ 'requests'|localize }}</h1>
    <SummaryCards :cards="summaryCards" :col-props="{cols: 12, sm: 6, md: 4}"/>
    <div class="white elevation-2 rounded">
      <v-row class="my-0" align="center">
        <v-col class="py-0">
          <v-tabs ref="tabs" v-model="tab" @change="updateRouteHash(tab)" background-color="transparent" class="grey--text text--lighten-1">
            <v-tab :ripple="false" href="#new" :disabled="loading" class="text-none">
              {{ 'new'|localize }}
              <v-badge v-if="counts.new" inline :color="tab === 'new' ? 'primary' : 'silver-chalice'" :content="counts.new" class="ml-2"/>
            </v-tab>
            <v-tab :ripple="false" href="#accepted_booked" :disabled="loading" class="text-none">
              {{ 'booked'|localize }}
              <v-badge v-if="counts.accepted_booked" inline :color="tab === 'accepted_booked' ? 'primary' : 'silver-chalice'" :content="counts.accepted_booked" class="ml-2"/>
            </v-tab>
            <v-tab :ripple="false" href="#all" :disabled="loading" class="text-none">
              {{ 'allRequests'|localize }}
              <v-badge v-if="counts.all" inline :color="tab === 'all' ? 'primary' : 'silver-chalice'" :content="counts.all" class="ml-2"/>
            </v-tab>
          </v-tabs>
        </v-col>
        <v-col cols="auto" class="py-0">
          <v-btn text color="primary" @click="resetFilters();clearRequestList();getRequests()" class="mr-2"><v-icon left>mdi-sync</v-icon> {{ 'resetFilters'|localize }}</v-btn>
        </v-col>
      </v-row>
      <v-divider class="mb-1"/>
      <v-row class="ma-0 mb-1">
        <v-col cols="4">
          <v-text-field
            :background-color="search ? 'serenade' : ''"
            v-model.trim="search"
            prepend-inner-icon="mdi-magnify"
            outlined
            hide-details
            dense
            clearable
            :placeholder="'search'|localize"
          />
        </v-col>
        <v-col cols="4">
          <v-autocomplete
            :background-color="filters.tags.length ? 'serenade' : ''"
            v-model="filters.tags"
            :items="tagsList"
            item-text="name"
            item-value="id"
            dense
            hide-details
            outlined
            :placeholder="'filterByTags'|localize"
            clearable
            multiple
            @change="clearRequestList();getRequests();"
          >
            <template v-slot:item="{ item }">
              <v-list-item-title>{{ item.name }}</v-list-item-title>
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0" class="text-truncate mr-1" style="max-width: 100px">{{ item.name }}</span>
              <span v-if="index === 1" class="grey--text text-caption mr-1">+{{ filters.tags.length - 1 }}</span>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
      <v-container class="py-0">
        <v-row class="my-0 mb-2">
          <v-col cols="3" class="py-0 pr-1">
            <v-autocomplete
              :background-color="filters.country_from.length ? 'serenade' : ''"
              v-model="filters.country_from"
              :items="COUNTRY_LIST"
              :item-text="item => `${item.name} ${item.code}`"
              :item-value="item => `${item.code}`"
              dense
              hide-details
              outlined
              :placeholder="'from'|localize"
              clearable
              multiple
              attach
              @change="clearRequestList();getRequests();"
            >
              <template v-slot:item="{ item, on }">
                <v-list-item-title>{{ item.name }} ({{item.code}})</v-list-item-title>
              </template>
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="text-truncate mr-1" style="max-width: 100px">{{ item.name }}</span>
                <span v-if="index === 1" class="grey--text text-caption mr-1">+{{ filters.country_from.length - 1 }}</span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="3" class="py-0 px-1">
            <v-autocomplete
              :background-color="filters.country_to.length ? 'serenade' : ''"
              v-model="filters.country_to"
              :items="COUNTRY_LIST"
              :item-text="item => `${item.name} ${item.code}`"
              :item-value="item => `${item.code}`"
              dense
              hide-details
              outlined
              :placeholder="'to'|localize"
              clearable
              multiple
              attach
              @change="clearRequestList();getRequests();"
            >
              <template v-slot:item="{ item, on }">
                <v-list-item-title>{{ item.name }} ({{item.code}})</v-list-item-title>
              </template>
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="text-truncate mr-1" style="max-width: 100px">{{ item.name }}</span>
                <span v-if="index === 1" class="grey--text text-caption mr-1">+{{ filters.country_to.length - 1 }}</span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col v-if="tab === 'all'" cols="3" class="py-0 px-1">
            <v-select
              :background-color="filters.status.length ? 'serenade' : ''"
              v-model="filters.status"
              :items="statusesList"
              item-text="name"
              item-value="value"
              dense
              hide-details
              outlined
              clearable
              multiple
              :placeholder="'status'|localize"
              @change="clearRequestList();getRequests();"
            >
              <template v-slot:item="{ item }">
                <v-icon :color="STATUS_COLORS[item.value]" size="20" class="mr-2">{{ STATUS_ICONS[item.value] }}</v-icon>
                <v-list-item-title colo>{{ item.value|localize }}</v-list-item-title>
              </template>
              <template v-slot:selection="{ item, index }">
                <v-icon v-if="index <= 2" :color="STATUS_COLORS[item.value]" size="20" class="mr-2">{{ STATUS_ICONS[item.value] }}</v-icon>
                <span v-if="index === 3" class="grey--text text-caption">+{{ filters.status.length - 3 }}</span>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row class="my-0 mb-2">
          <v-col cols="3" class="py-0 pr-1">
            <v-select
              :background-color="filters.special_requirements.length ? 'serenade' : ''"
              v-model="filters.special_requirements"
              :items="requirementsList"
              item-text="value"
              item-value="key"
              dense
              hide-details
              outlined
              clearable
              multiple
              :placeholder="'specialRequirements'|localize"
              @change="clearRequestList();getRequests();"
            >
              <template v-slot:item="{ item, on }">
                <v-icon class="mr-2" :color="SPECIAL_REQUIREMENTS_COLORS[item['key']]">{{ SPECIAL_REQUIREMENTS_ICONS[item['key']] }}</v-icon>
                <v-list-item-title>{{ item['key']|localize }}</v-list-item-title>
              </template>
              <template v-slot:selection="{ item, index }">
                <v-icon class="mr-2" :color="SPECIAL_REQUIREMENTS_COLORS[item['key']]">{{ SPECIAL_REQUIREMENTS_ICONS[item['key']] }}</v-icon>
              </template>
            </v-select>
          </v-col>
          <v-col cols="3" class="py-0 px-1">
            <v-menu
              ref="menuDate"
              v-model="menuDate"
              :close-on-content-click="false"
              :return-value.sync="filters.date"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :background-color="filters.date.length ? 'serenade' : ''"
                  :placeholder="'dateCreated' | localize"
                  :value="dateRangeText"
                  readonly
                  dense
                  hide-details
                  outlined
                  clearable
                  v-on="on"
                  @click:clear="clearRequestList();filters.date = [];getRequests()"
                />
              </template>
              <v-date-picker
                v-model="filters.date"
                no-title
                scrollable
                range
                @input="filters.date.length === 2 && $refs.menuDate.save(filters.date)"
                @change="clearRequestList();getRequests();"
              />
            </v-menu>
          </v-col>
          <v-col cols="3" class="py-0 px-1">
            <v-select
              :background-color="filters.request_type && 'serenade'"
              v-model="filters.request_type"
              :items="requestTypesList"
              item-text="name"
              item-value="value"
              dense
              hide-details
              outlined
              clearable
              :placeholder="'requestType'|localize"
              @change="clearRequestList();getRequests();"
            />
          </v-col>
          <v-col cols="3" class="py-0 pl-1">
            <v-select
              :background-color="filters.transport_mode.length ? 'serenade' : ''"
              v-model="filters.transport_mode"
              :items="transportModeList"
              item-text="name"
              item-value="value"
              dense
              hide-details
              outlined
              clearable
              multiple
              :placeholder="'transportMode'|localize"
              @change="clearRequestList();getRequests();"
            >
              <template v-slot:item="{ item, on }">
                <v-icon class="mr-2" color="silver-chalice">{{ TRANSPORT_ICONS[item['value']] }}</v-icon>
                <v-list-item-title>{{ item.value|localize }}</v-list-item-title>
              </template>
              <template v-slot:selection="{ item, index }">
                <v-icon v-if="index <= 2" class="mr-2" color="silver-chalice">{{ TRANSPORT_ICONS[item['value']] }}</v-icon>
                <span v-if="index === 3" class="grey--text text-caption">+{{ filters.transport_mode.length - 3 }}</span>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row class="my-0 mb-2">
          <v-col v-if="tab === 'new'" cols="4" class="py-0 pr-1">
            <v-switch v-model="filters.no_proposals" dense hide-details class="ma-0" :label="'noProposalsFilter'|localize" @change="clearRequestList();getRequests();"/>
          </v-col>
          <v-col v-if="tab === 'new'" cols="4" class="py-0 px-1">
            <v-switch v-model="filters.no_relevant_tc" dense hide-details class="ma-0" :label="'noRelevantTcFilter'|localize" @change="clearRequestList();getRequests();"/>
          </v-col>
          <v-col v-if="tab === 'all'" cols="4" class="py-0 pl-1">
            <v-switch v-model="closingSoonFilter" dense hide-details class="ma-0" :label="'closingSoonFilter'|localize"/>
          </v-col>
        </v-row>
      </v-container>
      <v-data-table
        :headers="headers"
        :items="requestList"
        :loading="loading"
        item-key="id + transport_mode"
        loader-height="2"
        :sort-by="defaultSortBy"
        :sort-desc="defaultSortDesc"
        :options.sync="options"
        :server-items-length="totalItems"
        hide-default-footer
      >
        <template v-slot:no-data>
          <EmptyState :icon="emptyState.icon" :title="emptyState.title|localize" :subtitle="emptyState.subtitle|localize">
            <v-btn v-if="isFiltered" text color="primary" @click="debouncedSearch='';resetFilters();clearRequestList();getRequests()">
              <v-icon left>mdi-sync</v-icon>{{ 'resetFilters'|localize }}
            </v-btn>
          </EmptyState>
        </template>
        <template v-slot:item.date_created="{ item }">
          <div>
            <div>{{ item.date_created }}</div>
            <!-- Fixed max-width + flex-wrap so tags wrap into extra rows instead of stretching the column; each chip truncates on its own so one long name can't blow it up either -->
            <div v-if="item.tags && item.tags.length" class="d-flex flex-wrap mt-1" style="max-width: 150px">
              <v-chip
                v-for="tag in item.tags.slice(0, 4)"
                :key="tag.id"
                x-small
                class="mr-1 mb-1"
                style="max-width: 140px"
              >
                <span class="text-truncate" :title="tag.name">{{ tag.name }}</span>
              </v-chip>
              <span v-if="item.tags.length > 4" class="grey--text text-caption align-self-center">+{{ item.tags.length - 4 }}</span>
            </div>
          </div>
        </template>
        <template v-slot:item.title="{ item }">
          <div class="py-2">
            <div>
              <v-tooltip v-for="type in item['special_requirements']" :key="type" top color="black">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon class="mr-1" v-bind="attrs" v-on="on" :color="SPECIAL_REQUIREMENTS_COLORS[type]">{{ SPECIAL_REQUIREMENTS_ICONS[type] }}</v-icon>
                </template>
                <span>{{ type|localize }}</span>
              </v-tooltip>
              <router-link class="black--text mr-1" style="vertical-align: middle" :to="{name: 'admin-request-details', query: {request_id: item.id}}">
                {{ item.title }}
              </router-link>
            </div>
            <div v-if="item['client_id']">
              <i class="name-col text-caption">{{ 'client'|localize }} ID: {{ item['client_id'] }}</i>
            </div>
            <div v-if="item['client']">
              <i class="name-col text-caption">{{ 'client'|localize }}: {{ item['client'] }}</i>
            </div>
            <div v-if="item['customer_ref']">
              <i class="name-col text-caption">{{ 'customerRef'|localize }}: {{ item['customer_ref'] }}</i>
            </div>
          </div>
        </template>
        <template v-slot:item.weight="{ item }">
          <span>{{ item.weight }} kg</span>
        </template>
        <template v-slot:item.request_type="{ item }">
          <span>{{ item.request_type|localize }}</span>
        </template>
        <template v-slot:item.transport_modes="{ item }">
          <v-tooltip v-for="mode in item['transport_modes']" :key="mode" top color="black">
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" :color="item['booked_transport_mode'] === mode ? 'primary' : 'silver-chalice'">{{ TRANSPORT_ICONS[mode] }}</v-icon>
            </template>
            <span>{{ mode|localize }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.relevant_tc_qty="{ item }">
          <div class="d-flex align-center">
            {{ item['proposals_count'] }}/{{ item['relevant_tc_qty'] }}
            <v-tooltip v-if="item['company'].length > 0" left color="black">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on"  size="20" color='silver-chalice' class="ml-2">mdi-information-outline</v-icon>
              </template>
              <template>
                <div class="py-1">
                  <h5 class="text-subtitle-2 mb-1">{{ 'companies'|localize }}</h5>
                  <ul class="px-2">
                    <li v-for="company in item['company']" :key="company.id">{{ company.name }}</li>
                  </ul>
                </div>
              </template>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:item.status="{ item }">
          <RequestStatusCell :requestData="item" @updateRequestStatus="handleStatusUpdate(item, $event)" :key="item.id" />
        </template>
        <template v-slot:item.tags="{ item }">
          <RequestTagsCell
            :requestData="item"
            :tagsList="tagsList"
            @updateRequestTags="item.tags = $event"
            @tagsChanged="getTags"
            :key="item.id"
          />
        </template>
      </v-data-table>
      <v-pagination
        color="primary"
        v-model="currentPage"
        :length="totalPages"
        :total-visible="7"
        class="py-6"
        :disabled="loading"
        @input="requestList = [];getRequests();"
      />
    </div>
  </v-container>
</template>

<script>
  import {TRANSPORT_ICONS, TRANSPORT_TYPES} from '@/enums/transportMode';
  import {STATUSES, STATUS_COLORS, STATUS_ICONS} from '@/enums/statuses';
  import { SPECIAL_REQUIREMENTS_COLORS, SPECIAL_REQUIREMENTS_ICONS } from '../enums/specialRequirements';
  import localize from '@/filters/localize';
  import api from '@/api/api';
  import methods from '@/helpers/methods';
  import RequestStatusCell from '@/components/RequestStatusCell.vue';
  import RequestTagsCell from '@/components/RequestTagsCell.vue';
  import EmptyState from '@/components/common/EmptyState';
  import SummaryCards from '@/components/common/SummaryCards';
  import filterRouteSync from '@/mixins/filterRouteSync';

  // Empty-state copy per tab, shown when a tab genuinely has no rows (see `emptyState` computed).
  const EMPTY_STATE_BY_TAB = {
    new: {icon: 'mdi-inbox-arrow-down-outline', title: 'emptyAdmReqNewTitle', subtitle: 'emptyAdmReqNewSubtitle'},
    accepted_booked: {icon: 'mdi-calendar-check-outline', title: 'emptyAdmReqBookedTitle', subtitle: 'emptyAdmReqBookedSubtitle'},
    all: {icon: 'mdi-inbox-outline', title: 'emptyAdmReqAllTitle', subtitle: 'emptyAdmReqAllSubtitle'},
  };

  export default {
    name: "AdminRequests",
    components: { RequestStatusCell, RequestTagsCell, EmptyState, SummaryCards },
    mixins: [filterRouteSync],

    data() {
      return {
        TRANSPORT_TYPES,
        TRANSPORT_ICONS,
        STATUSES,
        STATUS_ICONS,
        STATUS_COLORS,
        defaultTab: 'new',
        tab: this.initialHash('new'),
        // Set by goToTab() right before it changes `tab` on behalf of a summary card, so the `tab`
        // watcher below knows to skip its own reset+fetch (goToTab already does it, with the patch applied).
        suppressTabWatch: false,
        loading: false,
        debouncedSearch: '',
        debounceTimeout: null,
        defaultSortBy: 'date_created',
        defaultSortDesc: true,
        options: {},
        itemsPerPage: 20,
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        requestList: [],
        menuDate: false,
        filters: this.initialFilters(),
        summary: {
          without_relevant_tc: 0,
          closing_soon: 0,
          action_required: 0,
          quotes_expiring_soon: 0,
          need_response: 0,
        },
        counts: {}
      };
    },

    created() {
      this.getRequestsSummary();
    },

    mounted() {
      this.getTags();
      this.getRequestCounts();
    },

    watch: {
      tab() {
        if (this.suppressTabWatch) {
          this.suppressTabWatch = false;
          return;
        }
        this.resetTabScopedFilters();
        this.clearRequestList();
        this.getRequests();
      },
      options: {
        handler() {
          this.clearRequestList();
          this.getRequests();
        },
        deep: true,
      },
      search() {
        this.clearRequestList();
        this.getRequests();
      },
      filters: {
        handler() {
          this.handleFiltersQuery();
        },
        deep: true,
      },
    },

    computed: {
      summaryCards() {
        return [
          {
            key: 'without_relevant_tc',
            icon: 'mdi-truck',
            color: 'warning',
            count: this.summary.without_relevant_tc,
            label: localize('requestsWithoutRelevantTc'),
            actionLabel: localize('view'),
            onClick: () => this.goToTab('new', {no_relevant_tc: true}),
          },
          {
            key: 'closing_soon',
            icon: 'mdi-clock-alert-outline',
            color: 'error',
            count: this.summary.closing_soon,
            label: localize('requestsClosingSoon'),
            actionLabel: localize('view'),
            onClick: () => this.goToTab('all', {deadline_within_hours: 24}),
          },
          {
            key: 'action_required',
            icon: STATUS_ICONS[STATUSES.ACTION_REQUIRED],
            color: STATUS_COLORS[STATUSES.ACTION_REQUIRED],
            count: this.summary.action_required,
            label: localize('action_required'),
            actionLabel: localize('view'),
            onClick: () => this.goToAdminQuotes('all', {status: ['action_required']}),
          },
          {
            key: 'quotes_expiring_soon',
            icon: 'mdi-clock-alert-outline',
            color: 'error',
            count: this.summary.quotes_expiring_soon,
            label: localize('quotesExpiringSoon'),
            actionLabel: localize('view'),
            onClick: () => this.goToAdminQuotes('all', {expiring_soon: true}),
          },
          {
            key: 'need_response',
            icon: STATUS_ICONS[STATUSES.NEED_RESPONSE],
            color: STATUS_COLORS[STATUSES.NEED_RESPONSE],
            count: this.summary.need_response,
            label: localize('need_response'),
            actionLabel: localize('view'),
            onClick: () => this.goToTab('all', {status: ['need_response']}),
          },
        ];
      },
      // Boolean switch bound to the numeric `deadline_within_hours` filter (fixed at 24h, like the summary card).
      closingSoonFilter: {
        get() {
          return !!this.filters.deadline_within_hours;
        },
        set(value) {
          this.filters.deadline_within_hours = value ? 24 : null;
          this.clearRequestList();
          this.getRequests();
        },
      },
      COUNTRY_LIST() {
        return this.$store.getters.countries;
      },
      tagsList() {
        return this.$store.getters.tags;
      },
      SPECIAL_REQUIREMENTS_COLORS() {
        return SPECIAL_REQUIREMENTS_COLORS
      },
      SPECIAL_REQUIREMENTS_ICONS() {
        return SPECIAL_REQUIREMENTS_ICONS
      },
      transportModeList() {
        return this.$store.getters.transportModeList;
      },
      requirementsList() {
        return this.$store.getters.requirementsList;
      },
      requestTypesList() {
        return this.$store.getters.requestTypesList;
      },
      headers() {
        let localHeaders = [
          {text: 'ID', value: 'id', width: 80},
          {text: localize('dateCreated'), value: 'date_created', width: 170},
          {text: '', value: 'tags', width: 56, sortable: false},
          {text: localize('title'), value: 'title', width: 300},
          {text: localize('requestType'), value: 'request_type', width: 'auto'},
          {text: localize('transportMode'), value: 'transport_modes', width: 'auto'},
          {text: localize('quotes_companies'), value: 'relevant_tc_qty', width: 140},
          {text: localize('status'), value: 'status', width: 140},
        ];
        if (this.tab === 'new' || this.tab === 'accepted_booked') {
          localHeaders = localHeaders.filter((item) => item.value !== 'status');
        }
        return localHeaders;
      },
      statusesList() {
        let statuses = this.$store.getters.statuses;
        const validStatuses = [
          STATUSES.NEW,
          STATUSES.NEED_RESPONSE,
          STATUSES.CLOSED,
          STATUSES.DELETED,
          STATUSES.DRAFT,
          STATUSES.BOOKED,
          STATUSES.NOT_BOOKED,
          STATUSES.REJECTED,
          STATUSES.CANCELLED
        ];
        return statuses.filter(status => validStatuses.includes(status.value));
      },
      dateRangeText () {
        return this.filters.date.sort().join(' ~ ')
      },
      search: {
        get() {
          return this.debouncedSearch
        },
        set(val) {
          if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
          this.debounceTimeout = setTimeout(() => {
            this.debouncedSearch = val
          }, 500)
        }
      },
      isFiltered() {
        return Object.values(this.filters).some((value) => Array.isArray(value) ? value.length > 0 : !!value) || !!this.search;
      },
      emptyState() {
        if (this.isFiltered) {
          return {icon: 'mdi-filter-remove-outline', title: 'emptyFilteredTitle', subtitle: 'emptyFilteredSubtitle'};
        }
        return EMPTY_STATE_BY_TAB[this.tab] || {icon: 'mdi-inbox-outline', title: 'emptyGenericTitle', subtitle: 'emptyGenericSubtitle'};
      },
    },

    methods: {
      resetFilters() {
        this.filters.country_from = [];
        this.filters.country_to = [];
        this.filters.status = [];
        this.filters.transport_mode = [];
        this.filters.request_type = null;
        this.filters.date = [];
        this.filters.special_requirements = [];
        this.filters.tags = [];
        this.filters.no_proposals = false;
        this.filters.no_relevant_tc = false;
        this.filters.deadline_within_hours = null;
        this.clearFiltersQuery();
      },
      clearRequestList() {
        this.requestList = [];
        this.totalPages = 0;
        this.totalItems = 0;
        this.currentPage = 1;
      },
      setRequestsData(requestsData) {
        this.requestList = requestsData.data.data;
        this.totalPages = requestsData.data.pagination['total_page'];
        this.totalItems = requestsData.data.pagination['total_items'];
      },
      queryData() {
        let queryData = {};
        queryData['tab'] = this.tab;
        queryData['per_page'] = this.itemsPerPage;
        queryData['page'] = this.currentPage;
        this.options.sortBy[0] && (
          queryData['sort'] = {
            field: this.options.sortBy[0],
            desc: this.options.sortDesc[0],
          });
        this.search && (queryData['search'] = this.search);
        queryData.filters = {};
        this.filters.country_from.length && (queryData.filters.country_from = this.filters.country_from);
        this.filters.country_to.length && (queryData.filters.country_to = this.filters.country_to);
        this.filters.status.length && (queryData.filters.status = this.filters.status);
        this.filters.transport_mode.length && (queryData.filters.transport_mode = this.filters.transport_mode);
        this.filters.request_type && (queryData.filters.request_type = [this.filters.request_type]);
        this.filters.date.length && (queryData.filters.date = this.filters.date.sort());
        this.filters.special_requirements.length && (queryData.filters.special_requirements = this.filters.special_requirements);
        this.filters.tags.length && (queryData.filters.tags = this.filters.tags);
        this.filters.no_proposals && (queryData.filters.no_proposals = true);
        this.filters.no_relevant_tc && (queryData.filters.no_relevant_tc = true);
        this.filters.deadline_within_hours && (queryData.filters.deadline_within_hours = this.filters.deadline_within_hours);
        return queryData;
      },
      async getRequests() {
        try {
          this.loading = true;
          let requestsData = await api.getAdminRequests(this.queryData());
          this.setRequestsData(requestsData);
          this.loading = false;
        } catch (e) {
          this.loading = false;
        }
      },
      async getRequestsSummary() {
        try {
          let summaryData = await api.getAdminRequestsSummary();
          this.summary = summaryData.data;
        } catch (e) {}
      },
      async getRequestCounts() {
        try {
          let countsData = await api.getAdminRequestCounts();
          this.counts = countsData.data;
          // badges change tab widths; recompute the active-tab slider so the underline stays aligned
          this.$nextTick(() => this.$refs.tabs && this.$refs.tabs.callSlider());
        } catch (e) {
          // counts are a best-effort UI indicator: keep badges hidden if the endpoint is unavailable
        }
      },
      handleStatusUpdate(item, status) {
        item.status = status;
        this.getRequestCounts();
        this.getRequestsSummary();
      },
      async getTags() {
        try {
          await this.$store.dispatch('loadTags');
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        }
      },
      // Tab-scoped filters are cleared when the tab changes, so a filter enabled on one tab (e.g.
      // closing-soon on "all", or no-quotes / no-relevant-carrier on "new") can't stay silently
      // applied on another tab where its control isn't rendered. Card navigation re-applies its own
      // patch after this reset, via goToTab.
      resetTabScopedFilters() {
        this.filters.status = [];
        this.filters.no_proposals = false;
        this.filters.no_relevant_tc = false;
        this.filters.deadline_within_hours = null;
      },
      // Used by the summary cards: switches tab and (optionally) presets a filter, then syncs both into the URL.
      // Applies the filter patch itself (after the tab-scoped filter reset) and fetches once, instead of
      // relying on the `tab` watcher, so the patch can never be wiped by that watcher's own reset.
      goToTab(tab, filterPatch = {}) {
        const tabChanged = this.tab !== tab;
        if (tabChanged) {
          this.suppressTabWatch = true;
        }
        // Always reset tab-scoped filters first, so clicking a second summary card that targets the
        // same tab (e.g. "need response" then "closing soon", both on the "all" tab) replaces the
        // previous card's filter instead of stacking on top of it.
        this.resetTabScopedFilters();
        this.tab = tab;
        Object.assign(this.filters, filterPatch);
        this.clearRequestList();
        this.getRequests();
        this.updateRouteHash(tab);
      },
      // Used by the two summary cards that hand off to the admin-all-quotes page (a different route).
      // Only the patch is put in the URL (compacted); AdminQuotes.initialFilters() merges it over its
      // own defaults on load, so there is no need to spell out the full filters shape here.
      goToAdminQuotes(hash, filterPatch = {}) {
        this.$router.push({name: 'admin-all-quotes', hash: `#${hash}`, query: {filters: this.filtersQuery(filterPatch)}});
      },
      initialFilters() {
        const defaults = {
          country_from: [],
          country_to: [],
          status: [],
          transport_mode: [],
          request_type: null,
          date: [],
          special_requirements: [],
          tags: [],
          no_proposals: false,
          no_relevant_tc: false,
          deadline_within_hours: null,
        };
        const queryFilters = this.$router.currentRoute.query?.filters;
        if (queryFilters && typeof queryFilters === 'string') {
          try {
            // Merge over defaults so keys missing from a stale/partial URL stay present and reactive (Vue 2).
            return {...defaults, ...JSON.parse(queryFilters)};
          } catch (e) {
            // A malformed filters query must not crash the page (data() would throw); fall back to defaults.
            return defaults;
          }
        }
        return defaults;
      },
    },
  };
</script>
